import { AlertTriangle } from "@liftedcare/solidjs-untitled-icons/line";
import { Check, InfoCircle } from "@liftedcare/solidjs-untitled-icons/solid";
import { createSignal, For, ParentComponent, Show } from "solid-js";
import { createStore } from "solid-js/store";
import { Portal } from "solid-js/web";

import { Button } from "../common/Button.js";
import { IconGlow } from "../icons/IconGlow.js";
import { DEFAULT_AUTO_CLOSE_MS, IToastProps, Toast } from "./Toast.js";
import { ToastContext } from "./ToastContext.js";

const MAX_TOASTS = 3;
const maxToastsLength = MAX_TOASTS - 1;

// Create a random ID
function generateId() {
  const x = 2_147_483_648;
  return (
    Math.floor(Math.random() * x).toString(36) +
    Math.abs(Math.floor(Math.random() * x) ^ Date.now()).toString(36)
  );
}

export const ToastProvider: ParentComponent = (props) => {
  const [toasts, setToasts] = createStore<IToastProps[]>([]);
  const [stackParentTimeoutMs, setStackParentTimeoutMs] = createSignal<
    number | undefined
  >();

  const stackedToasts = () => {
    return toasts.filter((t) => t.stacked);
  };

  const open = (toast: Omit<IToastProps, "id">) => {
    const newToast: IToastProps = {
      id: generateId(),
      title: toast.title,
      content: toast.content,
      details: toast.details,
      iconComponent: toast.iconComponent,
      autoCloseMs:
        toasts.length < maxToastsLength ? toast.autoCloseMs : Infinity,
      closeable: toast.closeable,
      close: toast.close,
      className: toast.className,
      containerClassName: toast.containerClassName,
      stacked: toasts.length >= maxToastsLength,
      style: toast.style,
    };

    setToasts([...toasts, newToast]);

    if (stackedToasts().length) {
      // If we add to the stack restart the parent timeout. We have to toggle it to Infinity (or any value) so that solid recognises the change
      setStackParentTimeoutMs(Infinity);
      setStackParentTimeoutMs(10_000);
    }

    return newToast.id;
  };

  const info = (
    title: string,
    content: string,
    details?: string,
    closeable?: boolean,
    autoCloseMs?: number,
    className?: string,
    containerClassName?: string,
  ) => {
    return open({
      title,
      content,
      details,
      iconComponent: (
        <IconGlow
          glowColor="info"
          icon={<InfoCircle class="size-4 text-blue-500" />}
          size="sm"
        />
      ),
      autoCloseMs,
      closeable: closeable ?? true,
      className: `${className}`,
      containerClassName,
      style: "info",
    });
  };

  const warning = (
    title: string,
    content: string,
    details?: string,
    closeable?: boolean,
    autoCloseMs?: number,
    className?: string,
    containerClassName?: string,
  ) => {
    return open({
      title,
      content,
      details,
      iconComponent: (
        <IconGlow
          glowColor="warning"
          icon={<AlertTriangle class="size-4 text-yellow-500" />}
          size="sm"
        />
      ),
      autoCloseMs,
      closeable: closeable ?? true,
      className: `${className}`,
      containerClassName,
      style: "warning",
    });
  };

  const error = (
    title: string,
    content: string,
    details?: string,
    closeable?: boolean,
    autoCloseMs?: number,
    className?: string,
    containerClassName?: string,
  ) => {
    return open({
      title,
      content,
      details,
      iconComponent: (
        <IconGlow
          glowColor="error"
          icon={<AlertTriangle class="size-4 text-red-500" />}
          size="sm"
        />
      ),
      autoCloseMs,
      closeable: closeable ?? true,
      className: `${className}`,
      containerClassName,
      style: "error",
    });
  };

  const success = (
    title: string,
    content: string,
    details?: string,
    closeable?: boolean,
    autoCloseMs?: number,
    className?: string,
    containerClassName?: string,
  ) => {
    return open({
      title,
      content,
      details,
      iconComponent: (
        <IconGlow
          glowColor="success"
          icon={<Check class="size-4 text-success-500" />}
          size="sm"
        />
      ),
      autoCloseMs,
      closeable: closeable ?? true,
      className: `${className}`,
      containerClassName,
      style: "success",
    });
  };

  const close = (id?: string) => {
    if (!id) {
      return;
    }
    setToasts((currentToasts: IToastProps[]) =>
      currentToasts.filter((toast) => toast.id !== id),
    );
  };

  return (
    <ToastContext.Provider
      value={{
        toasts,
        open,
        close,
        info,
        warning,
        error,
        success,
      }}
    >
      {props.children}
      <Portal>
        <div
          class={`fixed right-0 z-[100] w-full max-sm:top-0 md:bottom-5 md:right-5 md:w-1/3 2xl:w-1/4 ${
            toasts.at(-1)?.containerClassName ?? ""
          }`}
        >
          <For each={toasts}>
            {(toast) => <Toast {...toast} close={() => close(toast.id)} />}
          </For>
          <Show when={stackedToasts().length}>
            <Toast
              id={"stacked-parent"}
              title={`${stackedToasts().length} more notifications`}
              content={
                <div class="flex w-full justify-center">
                  <Button
                    onClick={() => {
                      setToasts((currentToasts: IToastProps[]) =>
                        currentToasts.map((t) => ({
                          ...t,
                          stacked: false,
                          autoCloseMs: DEFAULT_AUTO_CLOSE_MS,
                        })),
                      );
                    }}
                    variant="text"
                    class="!font-normal !text-gray-700 hover:underline"
                  >
                    Click to view
                  </Button>
                </div>
              }
              iconComponent={
                <IconGlow
                  glowColor="info"
                  icon={<InfoCircle class="size-4 text-blue-500" />}
                  size="sm"
                />
              }
              close={() => {
                close("stacked-parent");
                for (const t of stackedToasts()) {
                  close(t.id);
                }
              }}
              closeable
              autoCloseMs={stackParentTimeoutMs()}
              style="info"
            />
          </Show>
        </div>
      </Portal>
    </ToastContext.Provider>
  );
};
